import React from 'react';
import {StyleSheet, ScrollView, View} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import uuid from 'src/nativeModules/UUID';
import ScreenContext from '../../ScreenContext';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import Events from 'src/logging/Events';
import KeyboardAvoidingView from '../../elements/365KeyboardAvoidingView';
import BirthMonthDropdown, {
  getMonthText,
} from '../../elements/account/BirthMonthDropdown';
import BirthYearDropdown from '../../elements/account/BirthYearDropdown';
import GenderDropdown from '../../elements/account/GenderDropdown';
import BackSubheader from '../../elements/BackSubheader';
import AVTextInput from '../../elements/AVTextInput';
import MenuOption from '../../elements/MenuOption';
import RoundedButton, {ButtonType} from '../../elements/RoundedButton';
import ShadowContainer from '../../elements/ShadowContainer';
import withIsConnected from '../../hoc/withIsConnected';
import Styles from '../../Styles';
import {IsConnectedProps} from 'src/types/Screen';
import ActionsFactory from 'src/actions/ActionsFactory';
import AccountStore from 'src/stores/AccountStore';
import LocaleDropdown from '../../elements/account/LocaleDropdown';
import InfoIcon from '../../elements/InfoIcon';
import Settings from 'src/Settings';
import Localized from 'src/constants/AppStrings';
import {alertError} from '../../helpers/AlertHelper';
import {connect} from 'react-redux';
import {AppDispatch} from 'src/redux/store';
import {hideSpinner, showSpinner} from 'src/redux/slices/screenSlice';
type ManageAccountProps = IsConnectedProps & {dispatch: AppDispatch};
type ManageAccountState = {
  firstName: string;
  lastName: string;
  gender: string;
  birthMonth: string | null;
  birthYear: string | null;
  jobTitle: string;
  locale: string | null;
};

const MAX_NAME_LENGTH = 20;

class ManageAccount extends React.Component<
  ManageAccountProps,
  ManageAccountState
> {
  static contextType = ScreenContext;

  constructor(props: ManageAccountProps) {
    super(props);
    this.handleChangeEmail = this.handleChangeEmail.bind(this);
    this.convertMonthToText = this.convertMonthToText.bind(this);
    this.getBirthdate = this.getBirthdate.bind(this);
    this.genderSelected = this.genderSelected.bind(this);
    this.birthMonthSelected = this.birthMonthSelected.bind(this);
    this.birthYearSelected = this.birthYearSelected.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.localeSelected = this.localeSelected.bind(this);
    this.state = {
      firstName: AccountStore.getFirstName(),
      lastName: AccountStore.getLastName(),
      gender: AccountStore.getGender(),
      birthMonth: AccountStore.getBirthMonth(),
      birthYear: AccountStore.getBirthYear(),
      jobTitle: AccountStore.getJobTitle(),
      locale: AccountStore.getLocale(),
    };
  }

  localeSelected(locale: string) {
    this.setState({
      locale,
    });
  }

  genderSelected(gender: string) {
    this.setState({
      gender,
    });
  }

  birthMonthSelected(birthMonth: string) {
    this.setState({
      birthMonth,
    });
  }

  birthYearSelected(birthYear: string) {
    this.setState({
      birthYear,
    });
  }

  getBirthdate(): string {
    const birthMonth = AccountStore.getBirthMonth();
    const birthYear = AccountStore.getBirthYear();
    const birthMonthText = this.convertMonthToText(birthMonth);
    return `${birthMonthText} ${birthYear || ''}`;
  }

  convertMonthToText(month) {
    if (!month) {
      return '';
    }

    return getMonthText(false, month);
  }

  handleSave() {
    const errorMessage = this.validate();

    if (errorMessage) {
      alertError(errorMessage);
    } else {
      this.saveChanges();
    }
  }

  validate() {
    if (!this.state.firstName || !this.state.lastName) {
      return Localized.Errors.all_fields_required;
    }

    return null;
  }

  saveChanges = async () => {
    const {dispatch} = this.props;
    dispatch(showSpinner());

    try {
      const response = await ActionsFactory.getAccountActions().updateInfo(
        AccountStore.getAccountId(),
        {
          firstName: this.state.firstName,
          lastName: this.state.lastName,
          gender: this.state.gender,
          birthMonth:
            this.state.birthMonth !== '00' ? +this.state.birthMonth : null,
          birthYear: +this.state.birthYear,
          jobTitle: this.state.jobTitle,
          locale: this.state.locale,
        },
      );

      if (response) {
        NavActions.pop();
      } else {
        alertError(Localized.Errors.unable_to_save_changes);
      }
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      Events.Error.trackEvent(
        'Exception',
        'ManageAccountScreen:SaveChanges',
        error.message ? error.message : error.toString(),
        guid,
      );
      alertError(Localized.Errors.unable_to_save_changes, guid);
    } finally {
      dispatch(hideSpinner());
    }
  };

  handleChangeEmail() {
    NavActions.push(AppRoutes.ChangeEmail);
  }

  getDisplayGender(value: string) {
    if (value === 'Male') {
      return Localized.Labels.male;
    } else if (value === 'Female') {
      return Localized.Labels.female;
    }

    return Localized.Labels.other;
  }

  render() {
    return (
      <BackSubheader title={Localized.Labels.personal_info}>
        <KeyboardAvoidingView behavior="height" insideTab>
          <ScrollView style={Styles.Style.flex}>
            <ShadowContainer
              style={[styles.content, Styles.Style.maxWidthContainer]}
            >
              <>
                <AVTextInput
                  label={Localized.Labels.first_name}
                  onChangeText={(text) =>
                    this.setState({
                      firstName: text,
                    })
                  }
                  maxLength={MAX_NAME_LENGTH}
                  value={this.state.firstName}
                  accessibilityLabel="First Name"
                  autoCapitalize="words"
                />
                <AVTextInput
                  label={Localized.Labels.last_name}
                  onChangeText={(text) =>
                    this.setState({
                      lastName: text,
                    })
                  }
                  maxLength={MAX_NAME_LENGTH}
                  value={this.state.lastName}
                  accessibilityLabel="Last Name"
                  autoCapitalize="words"
                />
                <GenderDropdown
                  selectedValue={this.state.gender}
                  onSelect={this.genderSelected}
                />
                <BirthMonthDropdown
                  selectedValue={this.state.birthMonth}
                  onSelect={this.birthMonthSelected}
                  showNumbers={false}
                />
                <BirthYearDropdown
                  selectedValue={this.state.birthYear}
                  onSelect={this.birthYearSelected}
                />
                <AVTextInput
                  label={Localized.Labels.job_title}
                  onChangeText={(text) =>
                    this.setState({
                      jobTitle: text,
                    })
                  }
                  value={this.state.jobTitle}
                  accessibilityLabel="Job Title"
                  autoCapitalize="words"
                />
                <View style={styles.localeContainer}>
                  <View style={styles.localeDropdown}>
                    <LocaleDropdown
                      selectedValue={this.state.locale}
                      onSelect={this.localeSelected}
                      style={Styles.Style.flex}
                    />
                  </View>
                  <InfoIcon
                    infoMessage={Localized.Labels.formatString(
                      Localized.Labels.system_language_explanation,
                      Settings.appDisplayName,
                    )}
                    style={styles.localeIcon}
                  />
                </View>
                <MenuOption
                  accessibilityLabel="Change Email"
                  label={Localized.Labels.change_email}
                  onPress={this.handleChangeEmail}
                  style={styles.menu}
                />
              </>
            </ShadowContainer>
          </ScrollView>
          {this.props.isConnected && (
            <RoundedButton
              buttonType={ButtonType.action}
              text={Localized.Buttons.save}
              onPress={this.handleSave}
              accessibilityLabel="Save"
            />
          )}
        </KeyboardAvoidingView>
      </BackSubheader>
    );
  }
}

const styles = StyleSheet.create({
  content: {
    paddingHorizontal: Styles.Spacing.m3,
    paddingTop: Styles.Spacing.m1,
  },
  menu: {
    height: Styles.Heights.h5,
    marginBottom: Styles.Spacing.m5,
  },
  localeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  localeDropdown: {
    flex: 1,
  },
  localeIcon: {
    marginLeft: Styles.Spacing.m2,
  },
});
const ConnectedManageAccount = connect(null, (dispatch: AppDispatch) => ({
  dispatch,
}))(ManageAccount);
export default withForwardedNavigationParams()(
  withIsConnected(ConnectedManageAccount),
);
